import { type Content, type Post, type PostPlatform, SHARE_PLATFORMS, type SharePlatformId } from '../api.ts';

/**
 * What a new post starts out as (Design reference § Platforms, § Dialogs):
 * which platform, and which of the content's formats suits it.
 */

/** § Platforms "Format preference"; Other takes the content's first format. */
export const PLATFORM_FORMATS: Readonly<Record<PostPlatform, readonly string[]>> = {
  linkedin: ['square', 'portrait', 'landscape', 'reel'],
  instagram: ['reel', 'portrait', 'square'],
  facebook: ['portrait', 'square', 'reel', 'landscape'],
  tiktok: ['reel'],
  youtube: ['landscape', 'reel'],
  x: ['landscape', 'square'],
  other: [],
};

/** The first preference the content has, else its first format; `null` without formats. */
export function defaultPostFormat(platform: PostPlatform, formats: readonly string[]): string | null {
  return PLATFORM_FORMATS[platform].find((format) => formats.includes(format)) ?? formats[0] ?? null;
}

/**
 * The platforms the share copy has a post for, in the order it wrote them:
 * every `#`–`###` heading that names one, as the server's `parseShareCopy`
 * reads them (letters and digits only; `twitter` is X).
 */
export function shareCopyPlatforms(text: string | null): SharePlatformId[] {
  if (text === null) return [];
  const found: SharePlatformId[] = [];
  for (const line of text.split(/\r?\n/)) {
    const heading = /^\s{0,3}#{1,3}\s+(.+?)\s*#*\s*$/.exec(line)?.[1];
    if (heading === undefined) continue;
    const key = normalise(heading);
    const platform = SHARE_PLATFORMS.find(({ label }) => {
      const name = normalise(label);
      return key === name || key.startsWith(`${name} `) || (name === 'x' && key.startsWith('twitter'));
    });
    if (platform !== undefined && !found.includes(platform.id)) found.push(platform.id);
  }
  return found;
}

/** The first share-copy platform without a post yet, else LinkedIn. */
export function defaultPostPlatform(content: Pick<Content, 'shareCopy'>, posts: readonly Post[]): PostPlatform {
  const taken = new Set(posts.map((post) => post.platform));
  return shareCopyPlatforms(content.shareCopy).find((platform) => !taken.has(platform)) ?? 'linkedin';
}

function normalise(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Where a post is made by hand: the button's name ("Open {label}") and the page it opens. */
export interface Composer {
  readonly label: string;
  readonly url: string;
}

/** § Platforms: the organic and the paid composer of each platform; Other has none. */
export const COMPOSERS: Readonly<
  Record<PostPlatform, { readonly organic: Composer; readonly paid: Composer } | null>
> = {
  linkedin: {
    organic: { label: 'LinkedIn', url: 'https://www.linkedin.com/feed/?shareActive=true' },
    paid: { label: 'Campaign Manager', url: 'https://www.linkedin.com/campaignmanager/' },
  },
  instagram: {
    organic: { label: 'Instagram', url: 'https://business.facebook.com/latest/composer' },
    paid: { label: 'Ads Manager', url: 'https://adsmanager.facebook.com/' },
  },
  facebook: {
    organic: { label: 'Facebook', url: 'https://business.facebook.com/latest/composer' },
    paid: { label: 'Ads Manager', url: 'https://adsmanager.facebook.com/' },
  },
  tiktok: {
    organic: { label: 'TikTok', url: 'https://www.tiktok.com/tiktokstudio/upload' },
    paid: { label: 'TikTok Ads Manager', url: 'https://ads.tiktok.com/' },
  },
  youtube: {
    organic: { label: 'YouTube', url: 'https://studio.youtube.com/' },
    paid: { label: 'Google Ads', url: 'https://ads.google.com/' },
  },
  x: {
    organic: { label: 'X', url: 'https://x.com/compose/post' },
    paid: { label: 'X Ads', url: 'https://ads.x.com/' },
  },
  other: null,
};

/** The composer a post is made in; `null` for Other. */
export function composerFor(platform: PostPlatform, paid: boolean): Composer | null {
  const composers = COMPOSERS[platform];
  if (composers === null) return null;
  return paid ? composers.paid : composers.organic;
}
